/**
 * Cluster 2 · Applying & getting in
 *
 * Contains: Entry requirements per curriculum, Admissions
 * competitiveness (top block), plus the second block —
 * International-student suitability, Application process.
 *
 * Extracted verbatim from DegreeDetail.jsx (Feb 2026 refactor).
 */
import { ClusterMember } from "@/components/app/degree/ClusterLayout";
import { Section, DataRow, Ev } from "@/components/app/degree/DegreeDetailBits";
import { AIInterpretationNote } from "@/components/app/AIInterpretationCard";
import CourseTipsCard from "@/components/app/degree/CourseTipsCard";
import DaadEntryProfile from "@/components/app/degree/DaadEntryProfile";
import UsEntryProfile from "@/components/app/degree/UsEntryProfile";
import AcademicFitPanel from "@/components/app/degree/AcademicFitPanel";
import CourseOfferStatus from "@/components/app/degree/CourseOfferStatus";
import ContextualOfferCard from "@/components/app/degree/ContextualOfferCard";
import { EntryTariffKisCard } from "@/components/app/degree/EntryTariffKisCard";
import HesaEntryProfile from "@/components/app/degree/HesaEntryProfile";
import HesaAdmissionsSnapshot, { SmallSample } from "@/components/app/degree/HesaAdmissionsSnapshot";
import { Card } from "@/components/ui/card";
import { CAODeadlineChip, CAOChangeOfMindBanner } from "@/components/app/CAODeadline";
import { UCASDeadlineChip } from "@/components/app/UCASDeadlineChip";
import { PTDeadlineChip } from "@/components/app/PTDeadlineChip";
import { UcasPathways } from "@/components/app/degree/HesaPhase1Bits";
import { resolveOfferRate, offerRateChipClasses, offerRateChipLabel, offerRateMetricLabel } from "@/lib/offerRate";
import { ExternalLink } from "lucide-react";
import RelatedGuideLink from "@/components/app/university/RelatedGuideLink";
import { usePrimers, primerCodeForCountry } from "@/lib/usePrimers";
import { UCAS_FEE_LABEL } from "@/lib/feeYear";

// Per-band IELTS floor: shown only next to a valid overall minimum, when it is a valid band not above it,
// and when it is evidenced (editor-entered with a parseable stamp, or course evidence on a non-baseline record).
export function perBandFloor(et) {
  if (!et) return null;
  const band = et.ielts_per_band_min;
  const overall = et.ielts_min ?? et.ielts_minimum;
  const ok = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 9;
  if (!ok(band) || !ok(overall) || band > overall) return null;
  const stamp = et.ielts_per_band_min__last_edited_at;
  const edited = typeof stamp === "string" && !Number.isNaN(Date.parse(stamp));
  const evidenced = !et.policy && et.evidence && typeof et.evidence === "object" && !!(et.evidence.url || et.evidence.source);
  return edited || evidenced ? band : null;
}

export default function Cluster2Applying({ ctx }) {
  const {
    d, uni, hs, ai, isHesa, isDaad, isScorecard,
    academicSignals, admissionEvidence, internationalHasData, format,
  } = ctx;
  // Country-level Apply primer link — mirrors `UniversityDetail.jsx`'s
  // Admission-tab link so applicants reach the same primer from any
  // degree page. `primerCodeForCountry` tolerates both 2-letter
  // ("UK") and full-name ("Sweden", "Hong Kong") country values.
  const primers = usePrimers();
  const primerCountryCode = primerCodeForCountry(uni?.country, primers.codes);
  const primerCountryName = primerCountryCode
    ? (primers.byCode[primerCountryCode]?.name || uni?.country)
    : null;
  const primerGuideTo = primerCountryCode ? `/apply/${primerCountryCode}` : null;
  return (
    <>
      <ClusterMember cluster={2}>
      <CourseTipsCard degreeId={d.id} />
      <Section id="entry" title="Entry requirements per curriculum">
        <DaadEntryProfile degree={d} />
        {isScorecard && <UsEntryProfile uni={uni} />}
        {/* Feb 2026 · Uni-wide range disclaimer — surfaces when we
            have context data on the parent uni but NO per-course
            offer yet. Deliberately loud so users don't mistake the
            uni-wide range for the specific course's offer. */}
        <CourseOfferStatus degree={d} uni={uni} />
        {/* Three-row A-Level breakdown — Advertised (cited) / Typical
            intake (HESA) / Uni-wide (family floor). Renders on every
            UK course where we have at least one signal; never
            collapses the three into each other. */}
        {uni?.country === "UK" && (academicSignals.advertised || academicSignals.intake || academicSignals.uniWide || academicSignals.ucasTariff) && (
          <AcademicFitPanel signals={academicSignals} />
        )}
        {/* Feb 2026 · Contextual offer widget — self-hides when the
            uni doesn't publish a policy. Sits right after the advertised
            offer so the "you might need less" moment lands where
            families are already parsing grades. */}
        {uni?.country === "UK" && <ContextualOfferCard degreeId={d.id} />}
        {/* Per-course UCAS tariff distribution from Discover Uni — shows
            the actual points profile of entrants to THIS course, self-hides
            when data missing. */}
        {uni?.country === "UK" && <EntryTariffKisCard degree={d} />}
        {/* Legacy "Typical offer" callout — only render when the
            AcademicFitPanel above didn't already surface the same
            citation. */}
        {d.hesa_stats?.typical_offer_text && !academicSignals.advertised?.evidence?.url && (
          <div
            data-testid="typical-offer-callout"
            className="mb-5 rounded-md border border-emerald-300 bg-emerald-50/70 p-4"
          >
            <div className="label-uppercase !text-[10px] text-emerald-800 mb-1">
              Typical offer (from {(() => {
                try {
                  const u = d.hesa_stats.course_page_source_url;
                  return u ? new URL(u).hostname.replace(/^www\./, "") : "the university's course page";
                } catch { return "the university's course page"; }
              })()})
            </div>
            <div className="text-sm text-emerald-900 leading-relaxed">
              {d.hesa_stats.typical_offer_text}
            </div>
            {d.hesa_stats.course_page_source_url && (
              <a
                href={d.hesa_stats.course_page_source_url}
                target="_blank"
                rel="noreferrer"
                data-testid="typical-offer-source-link"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-800 hover:underline"
              >
                Read the full requirements <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}
        {(!d.entry_requirements || Object.keys(d.entry_requirements).length === 0) && (
          <HesaEntryProfile
            hesa={d.hesa_stats}
            universityName={uni?.name}
            editorialALevel={d.entry_requirements?.a_level?.typical}
          />
        )}
        {Object.entries(d.entry_requirements || {})
          .filter(([curr]) => curr !== "qualifications_alternatives")
          .filter(([, r]) => r && typeof r === "object" && !Array.isArray(r))
          .filter(([curr, r]) => {
            if (curr !== "a_level") return true;
            const isJustPass = String(r?.typical || "").trim().toLowerCase() === "pass"
                            && String(r?.minimum || "").trim().toLowerCase() === "pass";
            const hasBetterOffer = d.entry_requirements?.ucas_tariff?.standard != null;
            return !(isJustPass && hasBetterOffer);
          })
          .map(([curr, r]) => {
          const CURR_LABEL = {
            a_level: "A-level",
            ib: "IB Diploma",
            gpa: "GPA",
            american_diploma: "American Diploma",
            atar: "ATAR (Australia)",
            cbse: "CBSE",
            isc: "ISC / CISCE",
            ap_composite: "AP composite",
            abitur: "Abitur (Germany, 1.0–4.0)",
            french_bac: "Baccalauréat (France)",
            hkdse: "HKDSE (Hong Kong)",
            suneung: "Suneung / CSAT (수능)",
            ebau: "EBAU / Selectividad (Spain, 0–14)",
            maturita: "Maturità (Italy, 60–100)",
            tolc: "TOLC (CISIA admission test, Italy)",
            cao: "CAO Points (Ireland, 0–625)",
            ucas_tariff: "UCAS Tariff",
            cnaes: "Nota de Candidatura (Portugal DGES, 0–200)",
            matura: "Matura / Bachillerato equivalent (Bologna standard)",
            meritvarde: "Meritvärde (Sweden Antagning, 0–22.5)",
            karaktersnitt: "Karaktersnitt (Norway Samordna opptak)",
            kvotient: "Adgangskvotient (Denmark Optagelse, 0–14)",
            ielts: "IELTS",
            toefl: "TOEFL",
            sat: "SAT",
            act: "ACT",
          };
          const label = CURR_LABEL[curr] || curr.replace("_", " ");
          const minValue = r?.minimum !== undefined ? r.minimum : r?.min;
          const isUcasTariff = curr === "ucas_tariff";
          const typicalLabel = isUcasTariff ? "Standard offer" : "Typical";
          const minLabel = isUcasTariff ? "Contextual" : "Minimum";
          const typicalRaw = isUcasTariff ? r?.standard : r?.typical;
          const minRaw = isUcasTariff ? r?.contextual : minValue;
          const suffix = isUcasTariff && (typicalRaw != null || minRaw != null) ? " pts" : "";
          return (
            <Card key={curr} className="p-4 border-stone-200 mb-3">
              <div className="font-underground-sc text-xs tracking-wider text-stone-500 flex items-center gap-1.5">
                {label}
                {r?.estimated === true && (
                  <span
                    data-testid={`entry-req-estimated-${curr}`}
                    title={
                      r?.source === "us-acceptance-rate-tier"
                        ? `Estimated from institutional acceptance rate (${
                            r.accept_rate != null
                              ? Math.round(r.accept_rate * 100) + "%"
                              : "n/a"
                          }). Not a published cutoff.`
                        : "Estimated — not published by the university."
                    }
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider uppercase bg-amber-100 text-amber-900 border border-amber-300"
                  >
                    est.
                  </span>
                )}
              </div>
              <div className="mt-2 grid md:grid-cols-2 gap-x-6 text-sm">
                <div><b>{typicalLabel}:</b> {typicalRaw != null ? `${typicalRaw}${suffix}` : "—"}</div>
                <div><b>{minLabel}:</b> {minRaw != null ? `${minRaw}${suffix}` : "—"}</div>
                {r.required_subjects?.length ? <div><b>Required:</b> {r.required_subjects.join(", ")}</div> : null}
                {r.preferred_subjects?.length ? <div><b>Preferred:</b> {r.preferred_subjects.join(", ")}</div> : null}
                {r.unacceptable_subjects?.length ? <div className="text-red-800"><b>Not accepted:</b> {r.unacceptable_subjects.join(", ")}</div> : null}
                {r.min_maths ? <div><b>Min maths:</b> {r.min_maths}</div> : null}
                {r.min_english ? <div><b>Min English:</b> {r.min_english}</div> : null}
              </div>
              <Ev ev={r.evidence} />
            </Card>
          );
        })}
        {d.entry_requirements?.qualifications_alternatives && (
          <Card
            className="p-4 border-stone-200 mb-3 bg-stone-50/50"
            data-testid="entry-alternatives-card"
          >
            <div className="font-underground-sc text-xs tracking-wider text-stone-500 flex items-center gap-1.5">
              Also accepted — equivalent qualifications
              <button
                type="button"
                data-testid="alt-parity-tooltip"
                className="group relative inline-flex items-center justify-center w-4 h-4 rounded-full border border-stone-300 text-[9px] text-stone-500 hover:border-[#4C1D95] hover:text-[#4C1D95] cursor-help"
                title="How these equivalents are chosen"
                onClick={(e) => e.preventDefault()}
              >
                ?
                <span className="pointer-events-none absolute left-0 top-5 z-20 hidden group-hover:block group-focus:block normal-case tracking-normal font-sans text-left w-72 rounded-md border border-stone-200 bg-white shadow-lg p-3 text-[11px] text-stone-700 leading-snug">
                  <strong className="block text-stone-900 mb-1">UCAS-tariff parity</strong>
                  UCAS assigns points to every mainstream qualification
                  (A-Level A* = 56 pts, IB HL 7 = 56 pts, BTEC D* = 56
                  pts, etc.). We convert the A-Level typical into total
                  tariff points and pick the equivalent value in each
                  other qualification that lands at the same
                  points-total.
                  <em className="block mt-1 text-stone-500">
                    Always confirm individual subject requirements with
                    the admissions office — some courses require
                    specific subjects (e.g. Maths) alongside the total.
                  </em>
                </span>
              </button>
            </div>
            <div className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {d.entry_requirements.qualifications_alternatives.ib && (
                <div data-testid="alt-ib">
                  <b>IB Diploma:</b>{" "}
                  {d.entry_requirements.qualifications_alternatives.ib}
                </div>
              )}
              {d.entry_requirements.qualifications_alternatives.btec && (
                <div data-testid="alt-btec">
                  <b>BTEC Extended Diploma:</b>{" "}
                  {d.entry_requirements.qualifications_alternatives.btec}
                </div>
              )}
              {d.entry_requirements.qualifications_alternatives.scottish_advanced && (
                <div data-testid="alt-scottish">
                  <b>Scottish Highers:</b>{" "}
                  {d.entry_requirements.qualifications_alternatives.scottish_advanced}
                </div>
              )}
              {d.entry_requirements.qualifications_alternatives.access && (
                <div data-testid="alt-access">
                  <b>Access to HE Diploma:</b>{" "}
                  {d.entry_requirements.qualifications_alternatives.access}
                </div>
              )}
            </div>
            <div className="text-[10px] font-mono text-stone-400 mt-2">
              Derived at UCAS-tariff parity from the A-level typical
              grade — confirm individual subject requirements with the
              admissions office.
            </div>
          </Card>
        )}
        {d.entry_requirements?.suneung && (
          <div
            data-testid="suneung-also-accepts-pill"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-stone-300 bg-[#F5F1EA] px-3 py-1.5 text-[12px] text-stone-800"
          >
            <span className="font-underground-sc tracking-wider text-[10px] text-stone-500">
              Also accepts
            </span>
            <span>
              수능 (Suneung){" · "}
              <strong>{d.entry_requirements.suneung.typical}</strong> typical
              {" · "}
              <strong>{d.entry_requirements.suneung.minimum ?? d.entry_requirements.suneung.min}</strong> minimum
            </span>
          </div>
        )}
        {(() => {
          const p2ok = hs.course_page_scrape_status === "ok";
          const showUnknown = isHesa && !p2ok;
          const val = (bool) => showUnknown
            ? "Not published"
            : (bool === true ? "Required" : bool === false ? "No" : "—");
          const foundationVal = d.foundation_year_available === true ? "Available"
            : d.foundation_year_available === false ? "Not available"
            : showUnknown ? "Not published" : "—";
          const uniLink = hs.course_meta?.assessments_url
            || hs.course_page_source_url
            || hs.institution?.provider_url;
          return (
            <div className="mt-3 text-sm" data-testid="entry-flags-row">
              <b>Interview:</b> {val(d.interview_required)} ·{" "}
              <b>Entrance test:</b> {d.entrance_test || (showUnknown ? "Not published" : "—")} ·{" "}
              <b>Portfolio:</b> {val(d.portfolio_required)} ·{" "}
              <b>Foundation year:</b> {foundationVal}
              {showUnknown && uniLink && (
                <>
                  {" · "}
                  <a
                    href={uniLink.startsWith("http") ? uniLink : `https://${uniLink}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="entry-flags-course-page-link"
                    className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                  >
                    Check the university course page <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </div>
          );
        })()}

        {d.english_test && (
          <div className="mt-6" data-testid="section-entry-english">
            <div className="label-uppercase text-stone-600 mb-2">English language requirement</div>
            <div className="grid md:grid-cols-2 gap-x-8">
              <DataRow k="IELTS typical" v={d.english_test.ielts_typical ?? "—"} />
              <DataRow k="IELTS minimum" v={d.english_test.ielts_min ?? d.english_test.ielts_minimum ?? "—"} />
              {perBandFloor(d.english_test) != null && (
                <DataRow k="IELTS minimum in each band" v={perBandFloor(d.english_test)} testId="english-ielts-per-band-min" />
              )}
              <DataRow k="TOEFL iBT typical" v={d.english_test.toefl_typical ?? "—"} />
              <DataRow k="TOEFL iBT minimum" v={d.english_test.toefl_min ?? d.english_test.toefl_minimum ?? "—"} />
              {(d.english_test.pte_typical || d.english_test.pte_min) && (
                <>
                  <DataRow k="PTE typical" v={d.english_test.pte_typical ?? "—"} />
                  <DataRow k="PTE minimum" v={d.english_test.pte_min ?? "—"} />
                </>
              )}
            </div>
            <Ev ev={d.english_test.evidence} />
          </div>
        )}
      </Section>

      <Section id="admissions" title="Admissions competitiveness">
        <AIInterpretationNote
          interpretation={ai.interpretation}
          sectionKey="admissions"
          degreeId={d.id}
          dataAvailable={
            d.admissions?.offer_rate != null ||
            d.admissions?.applications != null ||
            hs.typical_a_level != null ||
            hs.typical_tariff_points != null ||
            hs.entry_profile?.tariff?.median_tariff != null
          }
        />
        {isHesa && !d.admissions?.applications && (
          <HesaAdmissionsSnapshot
            hesa={d.hesa_stats}
            uniName={uni?.name}
            courseTitle={d.official_title}
            advertisedTariff={d.entry_requirements?.ucas_tariff}
          />
        )}
        {d.admissions?.applications ? (
          <div className="grid md:grid-cols-2 gap-x-8">
            <DataRow k="Applications" v={d.admissions.applications.toLocaleString()} />
            <DataRow k="Offers" v={d.admissions.offers != null ? d.admissions.offers.toLocaleString() : "—"} />
            <DataRow k="Enrolments" v={d.admissions.enrolments?.toLocaleString() ?? "—"} />
            {d.admissions.offer_rate != null && (
              <DataRow k="Offer rate" v={`${(d.admissions.offer_rate * 100).toFixed(1)}%`} />
            )}
            <DataRow k="Yield" v={d.admissions.acceptance_yield ? `${(d.admissions.acceptance_yield * 100).toFixed(0)}%` : "—"} />
            <DataRow k="Rolling" v={d.admissions.rolling ? "Yes" : "No"} />
            <DataRow k="Deadline" v={d.admissions.deadline} />
            <DataRow k="Intl quota" v={d.admissions.international_quota_note || "—"} />
          </div>
        ) : isDaad ? (
          <div className="text-sm text-stone-700" data-testid="daad-admissions-note">
            German public universities don't publish aggregate offer-rate
            stats per programme. International applicants apply via{" "}
            <b>uni-assist</b> (or direct to the institution) based on subject
            prerequisites; competitive subjects apply a <b>Numerus Clausus</b>{" "}
            (NC) grade cut-off set annually.
            {d.daad_stats?.application_deadline_text && (
              <div className="mt-2">
                <b>Application window:</b> {d.daad_stats.application_deadline_text}
              </div>
            )}
          </div>
        ) : isScorecard && uni?.scorecard_admissions ? (
          (() => {
            const sa = uni.scorecard_admissions;
            return (
              <div className="text-sm text-stone-800" data-testid="us-admissions-note">
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5">
                  {sa.acceptance_rate != null && (
                    <div>
                      <b>Institutional acceptance rate:</b>{" "}
                      {(sa.acceptance_rate * 100).toFixed(1)}%
                    </div>
                  )}
                  {sa.sat_25th && sa.sat_75th && (
                    <div><b>SAT 25-75 %ile:</b> {sa.sat_25th}-{sa.sat_75th}</div>
                  )}
                  {sa.act_25th && sa.act_75th && (
                    <div><b>ACT 25-75 %ile:</b> {sa.act_25th}-{sa.act_75th}</div>
                  )}
                </div>
                <div className="mt-3 text-xs text-stone-500 italic">
                  US universities publish admissions data at the
                  institution level — no per-programme yield/offer split
                  exists federally. Competitive schools within the
                  institution (engineering, business, arts) often apply
                  stricter cut-offs than the institutional median.
                </div>
              </div>
            );
          })()
        ) : (
          <div className="text-sm text-stone-500 italic" data-testid="admissions-no-course-data">
            {(() => {
              const or = resolveOfferRate({ degree: d, uni });
              if (!or) return "Per-course applications/offers not published — no verified university-level fallback available.";
              const isStub = or.source === "legacy_editorial_stub";
              const stubNote = isStub
                ? " (this is a seed default awaiting verification — treat as directional only)"
                : "";
              const verifiedAt = or.evidence?.extracted_at;
              let daysAgo = null;
              if (verifiedAt) {
                try {
                  const diff = Date.now() - new Date(verifiedAt).getTime();
                  daysAgo = Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
                } catch { /* ignore */ }
              }
              return (
                <span>
                  Per-course applications/offers not published — falling back to{" "}
                  <span
                    title={or.label}
                    className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[10px] font-underground-sc tracking-wider not-italic ${offerRateChipClasses(or.source)}`}
                  >
                    {offerRateChipLabel(or.source)}
                  </span>{" "}
                  <span className="not-italic">{offerRateMetricLabel(or)}:</span>{" "}
                  {(or.value * 100).toFixed(1)}%
                  {daysAgo !== null && (
                    <span
                      data-testid="offer-rate-freshness"
                      className="ml-1 text-[10px] font-underground-sc tracking-wider not-italic text-stone-500"
                      title={new Date(verifiedAt).toLocaleString()}
                    >
                      · verified {daysAgo === 0 ? "today" : `${daysAgo}d ago`}
                    </span>
                  )}
                  {stubNote}.
                </span>
              );
            })()}
          </div>
        )}
        <Ev ev={d.admissions?.evidence || admissionEvidence} />
      </Section>
      </ClusterMember>

      <ClusterMember cluster={2}>
      {internationalHasData && (
      <Section id="international" title="International-student suitability">
        <AIInterpretationNote
          interpretation={ai.interpretation}
          sectionKey="international"
          degreeId={d.id}
          dataAvailable={
            d.international?.visa_eligible != null ||
            d.international?.psw_eligible_years != null ||
            d.international?.stem_designated != null ||
            d.international?.shortage_occupation != null ||
            !!d.international?.sponsorship_prospects_note
          }
        />
        <div className="grid md:grid-cols-2 gap-x-8">
          <DataRow k="Student visa eligible" v={d.international?.visa_eligible ? "Yes" : "No"} />
          <DataRow k="Post-study work (yrs)" v={d.international?.psw_eligible_years ?? "—"} />
          <DataRow k="STEM-designated" v={d.international?.stem_designated ? "Yes" : "No"} />
          <DataRow k="Shortage occupation" v={d.international?.shortage_occupation ? "Yes" : "No"} />
        </div>
        {d.international?.security_clearance_restrictions?.length ? (
          <div className="mt-3 text-sm text-amber-800"><b>Security clearance:</b> {d.international.security_clearance_restrictions.join(" ")}</div>
        ) : null}
        {d.international?.home_country_recognition && (
          <div className="mt-3">
            <div className="label-uppercase !text-[10px]">Home-country recognition</div>
            <ul className="text-sm mt-1">
              {Object.entries(d.international.home_country_recognition).map(([k, v]) => <li key={k}>· {k}: {v}</li>)}
            </ul>
          </div>
        )}
        {d.international?.sponsorship_prospects_note && (
          <div className="mt-3 text-sm"><b>Sponsorship prospects:</b> {d.international.sponsorship_prospects_note}</div>
        )}
        {d.source_provenance?.source === "daad_intl_programmes" && (
          <div
            data-testid="de-psw-note"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50/60 p-3 text-sm text-stone-800"
          >
            <b>Germany post-study rights:</b> non-EU graduates receive an
            18-month residence permit to seek graduate work (§20 AufenthG).
            MINT fields (engineering, IT, natural sciences, medicine) count
            as shortage-relevant under the Fachkräfteeinwanderungsgesetz —
            employer sponsorship for a Blue Card is comparatively fast.
            <a
              href="https://www.make-it-in-germany.com/en/visa-residence/types/study"
              target="_blank"
              rel="noreferrer"
              className="ml-1 underline decoration-dotted text-amber-900"
            >
              Federal guide
            </a>
          </div>
        )}
      </Section>
      )}

      <Section id="application" title="Application process">
        {/* Ireland CAO surfacing */}
        <div className="mb-3 flex flex-wrap gap-2 items-center">
          <CAODeadlineChip degree={d} />
          <UCASDeadlineChip degree={d} />
          <PTDeadlineChip degree={d} />
        </div>
        <CAOChangeOfMindBanner degree={d} />
        <UcasPathways
          pathways={d.hesa_stats?.ucas_pathways}
          uniName={uni?.name}
          courseTitle={d.official_title}
        />
        {(() => {
          const app = d.application || {};
          const stampedDeadline = d.application_deadline;
          const stampedLate     = d.late_application_deadline;
          const stampedCycle    = d.cycle_label;
          const isOxbridge = /^uk-hesa-1000(7774|7788)-/.test(d.id || "");
          const cah = hs.leo_sec?.cah_matched || hs.leo_sec?.cah_full || "";
          const isMedDentVet = /^CAH01/.test(cah);
          const isEarlyDeadline = isOxbridge || isMedDentVet;
          const platform = app.platform || (isHesa ? "UCAS" : "—");
          const opens    = app.opens    || (isHesa
            ? (stampedCycle ? "May 2026 (for 2027 entry)" : "Mid-September (prior year)")
            : "—");
          const closes   = app.closes || (isHesa
            ? (stampedDeadline
                ? `${stampedDeadline}${stampedCycle ? ` (for ${stampedCycle})` : ""}${isEarlyDeadline ? " · Oxbridge / Medicine / Dentistry / Vet" : " · UCAS equal-consideration"}`
                : (isEarlyDeadline ? "15 October (Oxbridge / Medicine / Dentistry / Vet)" : "14 January (UCAS equal-consideration)"))
            : "—");
          const lateClose = stampedLate && isHesa
            ? `${stampedLate} · UCAS late window`
            : null;
          const ps       = app.personal_statement_required === false ? "No"
            : app.personal_statement_required === true ? "Required"
            : isHesa ? "Required (via UCAS)" : "—";
          const refs     = app.references_required || (isHesa ? "1 (via UCAS)" : "—");
          const suppEssays = app.supplementary_essays
            || (isOxbridge ? "Course-specific written work (Oxbridge)" : (isHesa ? "See course page" : "—"));
          const appFee   = app.application_fee_usd
            ? format(app.application_fee_usd)
            : isHesa ? (d.fee_outlook?.ucas_fee_label ?? UCAS_FEE_LABEL) : "—";
          const decisionBy = app.decision_by || (isHesa
            ? (isEarlyDeadline ? "By early January" : "By 15 May (UCAS)")
            : "—");
          const deposit  = app.deposit_deadline || (isHesa ? "Set by university on offer" : "—");
          const deferral = app.deferral_policy || (isHesa ? "Usually allowed — request via UCAS Track" : "—");
          return (
            <div className="grid md:grid-cols-2 gap-x-8" data-testid="application-grid">
              <DataRow k="Platform" v={platform} />
              <DataRow k="Opens" v={opens} />
              <DataRow k="Closes" v={closes} />
              {lateClose && <DataRow k="Late window closes" v={lateClose} />}
              <DataRow k="Personal statement" v={ps} />
              <DataRow k="References" v={refs} />
              <DataRow k="Supplementary essays" v={suppEssays} />
              <DataRow k="Application fee" v={appFee} />
              <DataRow k="Decision by" v={decisionBy} />
              <DataRow k="Deposit deadline" v={deposit} />
              <DataRow k="Deferral policy" v={deferral} />
            </div>
          );
        })()}
        <Ev ev={d.application?.evidence
          || (isHesa ? { source: "UCAS 2027 admissions cycle · UK national defaults", url: "https://www.ucas.com/undergraduate/applying-university/ucas-undergraduate-when-apply", confidence: "verified" } : null)} />
      </Section>

      {/* Country-level "How to apply" primer — mirrors the link on
          UniversityDetail. Hidden when we don't have a primer for
          this country (RelatedGuideLink fails-closed on null `to`). */}
      <div className="mt-8">
        <RelatedGuideLink
          to={primerGuideTo}
          eyebrow="Country-level guide"
          title={primerCountryName ? `How to apply in ${primerCountryName}` : null}
          blurb="Routes, deadlines, visas and English requirements — written for international applicants."
          testId="degree-detail-apply-country-primer-link"
        />
      </div>
      </ClusterMember>
    </>
  );
}
